import { useSelector } from "react-redux"
import { RootState } from "../../app/store"
import { alertTypes } from "../AlertElement"
import { Dispatch, UnknownAction } from "@reduxjs/toolkit"
import { createOrUpdateElementData, updateFormLoading, updateFormMessage } from "./formsSlice"
import BaseApp, { ISearchParam } from "../../features/BaseApp"
import { IAxiosDefaultDataObject } from "../../config"
import { GridDataColumns } from "../GridData"

/**
 * Form Alert types
 */
export type FormAlertTypes = "error" | "warning" | "info" | "success"

export type FormElementsTypes = "TextField" | "Select" | "ImageUploader" | "SelectLocation" | "SelectWarehouse" | "SelectCategory" | "SelectSection" | "SelectItem" | "SelectUser" | "DateAndTime" | "Date" | "DynamicSelect" | "BlockEditor" | "DynamicModelSelect" | "GoogleMap" | "Checkbox" | "SelectCountry" | "Images"

export interface ISelectFieldSingleItem {
    name: string,
    value: any
}

export interface IDynamicFormElement {
    [key: string]: any,
    id: string,
    type: FormElementsTypes,
    xs: number,
    fieldType?: string,
    multiline?: boolean,
    maxRows?: number,
    minRows?: number,
    label?: string,
    fullWidth?: boolean,
    items?: Array<ISelectFieldSingleItem>,
    placeholder?: string,
    handleKeyUp?: (e: React.KeyboardEvent<HTMLDivElement>) => void
}

export interface IBaseDynamicFormElement extends IDynamicFormElement {
    handleOnChange: (id: string, value: any) => void,
    data: ISingleFormElementData,
    validateInput?: (value: string) => boolean,
    searchParams?: Array<ISearchParam>,
    disabled?: boolean,
    itemsList?: () => IAxiosDefaultDataObject[],
    modelTitleParam?: string,
    appClass?: BaseApp,
    addNewItem?: (payload: IAxiosDefaultDataObject) => UnknownAction
    updateItem?: (payload: IAxiosDefaultDataObject) => UnknownAction,
    selectLabel?: string,
    gridColumns?: GridDataColumns,
    filterForm?: IDynamicForm,
    updateSearchParamsBeforeLoadItems?: (params: ISearchParam[]) => ISearchParam[],
    disableAddNewItem?: boolean,
    enableGroups?: boolean,
    endAdornment?: JSX.Element,
}

export interface ISingleFormElementData {
    name: string,
    value: any,
    displayHelperText?: boolean,
    helperTextType?: "error" | "info",
    helperText?: string,
    disabled?: boolean
}

export interface IFormElementsData {
    [key: string]: ISingleFormElementData
}

export interface IFormMessage {
    shown: boolean,
    type: alertTypes,
    message: string
}

export interface IDynamicForm {
    name: string,
    elements: Array<IDynamicFormElement>,
    message?: IFormMessage,
    loading?: boolean
}

export interface IRequiredField {
    id: string,
    message: string,
    customValidate?: (val: any) => boolean,
}

export interface IVerifyFormResponse {
    isValid: boolean,
    fields: Array<IRequiredField>
}

/**
 * Return all data
 */
const useReturnAllFormsData = () => useSelector((state: RootState) => state.forms.data)

/**
 * Return single form element data
 */
const useReturnSingleFormElementData = (id: string) => {

    let details: ISingleFormElementData = {
        name: "",
        value: ""
    }

    const data = useSelector((state: RootState) => state.forms.data)

    if (data){
        if (data[id]!){
            details = data[id]
        }
    }

    return details
}

/**
 * Return single form element data
 */
const returnSingleFormElementData = (id: string, data: IFormElementsData) => {

    let details: ISingleFormElementData = {
        name: "",
        value: ""
    }

    if (data){
        if (data[id]!){
            details = data[id]
        }
    }

    return details
}

/**
 * Return single form
 */
const useReturnSingleForm = (name: string) => {

    const forms = useSelector((state: RootState) => state.forms.forms)

    let form: IDynamicForm = {
        name: "",
        elements: []
    }

    if (forms.length > 0){
        forms.map(singleForm => {
            if (singleForm.name === name){
                form = singleForm
            }
        })
    }

    return form

}

/**
 * Update form message
 */
const createOrUpdateFormMessage = (formName: string, message: IFormMessage, dispatch: Dispatch<UnknownAction>) => dispatch(updateFormMessage({ formName: formName, message: message }))

/**
 * Update form message
 */
const updateFormLoadingStatus = (formName: string, loading: boolean, dispatch: Dispatch<UnknownAction>) => dispatch(updateFormLoading({ formName: formName, loading: loading }))

/**
 * Verify Form
 */
const verifyForm = (data: IFormElementsData, requiredFields: Array<IRequiredField>) => {

    let response: IVerifyFormResponse = {
        isValid: true,
        fields: []
    }

    if (requiredFields && requiredFields.length > 0){
        let fields: Array<IRequiredField> = []

        requiredFields.map((field) => {
            if (data[field.id]){

                if (field.customValidate){
                    if (!field.customValidate(data[field.id].value)){
                        fields.push({
                            id: field.id,
                            message: field.message
                        })
                    }
                }else{
                    if (!data[field.id].value){
                        fields.push({
                            id: field.id,
                            message: field.message
                        })
                    }
                }

            }
        })

        response.fields = fields
        response.isValid = fields.length > 0 ? false : true

    }

    return response
}

/**
 * Update required fields helper text
 */
const updateRequiredFieldsHelperText = (fields: Array<IRequiredField>, data: IFormElementsData, dispatch: Dispatch<UnknownAction>) => {
    if (fields && fields.length > 0){
        fields.map((field) => {
            const element = returnSingleFormElementData(field.id, data)
            dispatch(createOrUpdateElementData({
                ...element,
                helperText: field.message,
                helperTextType: "error",
                displayHelperText: true
            }))
        })
    }
}

/**
 * Reset required fields helper text
 */
const resetRequiredFieldsHelperText = (fields: Array<IRequiredField>, data: IFormElementsData, dispatch: Dispatch<UnknownAction>) => {
    if (fields && fields.length > 0){
        fields.map((field) => {
            const element = returnSingleFormElementData(field.id, data)
            dispatch(createOrUpdateElementData({
                ...element,
                helperText: "",
                helperTextType: "info",
                displayHelperText: false
            }))
        })
    }
}

/**
 * Dyanmic Form
 */
const DynamicForm = {
    useReturnAllFormsData,
    useReturnSingleFormElementData,
    returnSingleFormElementData,
    useReturnSingleForm,
    createOrUpdateFormMessage,
    updateFormLoadingStatus,
    verifyForm,
    updateRequiredFieldsHelperText,
    resetRequiredFieldsHelperText
}
export default DynamicForm